import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { crossLetter, lagoonHers, neckLeft, neckStage, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** Three wolves' heads in a row: a band of the pack. */
const BAND = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 12l1.4-4.6L1.6 4l2 1.4L6 3.6l.6 2.8 1.6.8-1.8 1.4L5.6 12zM9.4 12l1.4-4.6L10 4l2 1.4L14.4 3.6l.6 2.8-1.8 1.8.4 3.8z" fill="currentColor"/></svg>';
/** A cairn of piled stones: Sigrun's camp. */
const CAIRN = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="13" rx="6.2" ry="2" fill="currentColor"/><ellipse cx="7.4" cy="9.6" rx="4.6" ry="1.9" fill="currentColor"/><ellipse cx="8.4" cy="6.6" rx="3.2" ry="1.5" fill="currentColor"/><ellipse cx="7.8" cy="4" rx="1.9" ry="1.2" fill="currentColor"/></svg>';
/** A crack running through ice. */
const CRACK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 2.5l3.4 3.2-1 2.6 3.6 1.4-.6 2.6L14 14" stroke="currentColor" stroke-width="1.7" fill="none" stroke-linecap="round" stroke-linejoin="round"/><path d="M5.4 5.7L8 4.2M7 9.7l2.8-.8" stroke="currentColor" stroke-width="1.1" fill="none" stroke-linecap="round"/></svg>';
/** A neck of ice between a floe and the shore. */
const NECK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 4.5h5l2 3 2-3h5v3l-5 1-2 3-2-3-5-1z" fill="currentColor"/></svg>';
/** A boot's print with the dot of its tack. */
const BOOT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6.4 1.6c2.2 0 3.6 1.8 3.6 4.4 0 2-.8 3.2-.8 4.6 0 1.8 1.2 2.6 1.2 3.4 0 .8-.8 1.4-2.4 1.4s-3-.8-3-2.6c0-1.6.6-2.8.4-4.6C5.2 6.6 3.6 5.6 3.6 4c0-1.6 1.2-2.4 2.8-2.4z" fill="currentColor"/><circle cx="8.6" cy="12.8" r="1.1" fill="#14161b"/></svg>';
/** Two tracks crossing. */
const CROSS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 13.5l11-11M2.5 2.5l4 4M9.5 9.5l4 4" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>';
/** A wolf's head (the pack off the shore). */
const WOLF = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13.5l1.8-5.2L3.6 4l2.6 1.8L9.4 2l.8 3.4 3.3 1.4-2.7 1.4L9 13.5z" fill="currentColor"/></svg>';
/** The Ash Seal: a long body belly-down, its head up. */
const SEAL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 12.5c2.2-.2 3.6-1.6 5.6-2.6 2.4-1.2 4.4-1.2 5.2-3.2.4-1 .2-2.4 1.2-3 .8.6 1 1.8.8 2.8 1 .2 1.4 1 .8 1.6-.8.8-2 .6-2.6 1.6-1 1.6-2.2 3.6-5 4.2-2.2.4-4.2-.2-6-1.4z" fill="currentColor"/></svg>';
/** A campfire. */
const FIRE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5c.6 2.2 3.4 3.6 3.4 6.6A3.4 3.4 0 0 1 4.6 8c0-1.6.8-2.4 1.6-3.2.2 1.2.8 2 1.6 2.2C7.4 5.2 7.2 3.4 8 1.5z" fill="currentColor"/><path d="M2.5 14.5l11-2M2.5 12.5l11 2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** A howl. */
const HOWL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13.5l1.8-5.2L3.6 4l2.6 1.8L9.4 2l.8 3.4 3.3 1.4-2.7 1.4L9 13.5z" fill="currentColor"/><path d="M12.8 2.6c.9.7 1.4 1.6 1.4 2.6M11.9 1.2c1.5.9 2.4 2.4 2.5 4" stroke="currentColor" stroke-width="1" fill="none" stroke-linecap="round"/></svg>';
/** An open crack in the ice: dark water. */
const HOLE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.6c.4-2.6 2.6-4.2 5.2-4 2.8.2 4.8 2 4.6 4.2-.2 2.2-2.8 3.4-5.4 3.2C4.6 11.8 2.8 10.6 3 8.6z" fill="currentColor"/></svg>';

type Mark = { el: HTMLButtonElement; state: HTMLElement; point: THREE.Vector3; at: number; kind: string; chip?: HTMLElement };

/** Her tracks' letters (A to F), the Wolf's and the pack's names. */
const letterName = (l: number): string => (l >= 0 && l < 6 ? String.fromCharCode(65 + l) : l === 6 ? t('senda.isla.wolf') : t('senda.isla.pack'));

/**
 * The White Island on screen (docs/sendas/barbaro.md, chapter 4), inside a path's own plate (game/senda-hud.ts) and over
 * the board; a phone's plate kept under about a quarter of the screen:
 *
 * - the floes (`floes`): the band (its number, how many on the ice, the next one's seconds), the camp's blows, the crack
 *   under the hero (its seconds), the three necks (a pip each: joined, creaking, parted), the rule. Over the board, the
 *   crack's mark as it creaks, and each neck's while it creaks or is parted (its seconds).
 * - the lagoon (`lagoon`): her crossings read, the cracks made (the next sinks), the wolves' seconds, the rule. Over the
 *   board, her six ends (a letter each: a tap walks there and breaks the ice; cracked, sunk), and each crossing: unread
 *   (a stop there reads it, its frost brushed off, a ring filling), read (who walks over whom, by letter), gone.
 * - Sigrun's last night (`seal`): the Seal's life and what it's doing (lining up at you or at the fire, sliding, eyeing
 *   a crack, under, cut off), the next slide's seconds, a howl to cut, the fire's blows, the rule. Over the board, the
 *   open cracks: the one it goes for in red (held at the screen's edge when it's off it).
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class IslaHud {
  readonly panel: HTMLElement;
  private kind: 'floes' | 'lagoon' | 'seal';
  private v = new THREE.Vector3();
  private rows: HTMLElement[] = [];
  private marks: Mark[] = [];
  private key = '';
  private keptRow = h('div.bh-row.bt-boatrow');
  private keptFill = h('i');
  private keptText = h('small.vt-boat-s');
  // The floes.
  private bandRow = h('div.bh-row.al-waverow.ix-band');
  private bandPips: HTMLElement[] = [];
  private bandText = h('span.al-wave');
  private crackRow = h('div.bh-row.al-waverow.ix-crack');
  private crackText = h('span.al-wave');
  private neckRow = h('div.bh-row.ix-necks');
  private neckPips: HTMLElement[] = [];
  // The lagoon.
  private readRow = h('div.bh-row.al-waverow.ix-read');
  private readText = h('span.al-wave');
  private wrongRow = h('div.bh-row.al-waverow.ix-wrong');
  private wrongText = h('span.al-wave');
  private wolfRow = h('div.bh-row.al-waverow.ix-wolves');
  private wolfText = h('span.al-wave');
  // The Seal.
  private sealRow = h('div.bh-row.bt-elder.ix-seal');
  private sealFill = h('i');
  private sealText = h('small.vt-boat-s');
  private slideRow = h('div.bh-row.al-waverow.bt-leap.ix-slide');
  private slideText = h('span.al-wave');
  private howlRow = h('div.bh-row.al-waverow.bt-leap.ix-howl');
  private howlText = h('span.al-wave');

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const ir = s.rules?.senda?.isla;
    this.kind = ir?.floes ? 'floes' : ir?.lagoon ? 'lagoon' : 'seal';
    if (ir?.floes) {
      const r = ir.floes;
      this.keptRow.append(h('span.bh-ico', { html: CAIRN }), h('span.bh-label', {}, t('senda.isla.camp')), h('span.bar.vt-bar', {}, this.keptFill), this.keptText);
      this.bandPips = r.bands.map(() => h('i'));
      this.bandRow.append(h('span.bh-ico', { html: BAND }), h('span.al-pips.vt-pips', {}, ...this.bandPips), this.bandText);
      this.crackRow.append(h('span.bh-ico', { html: CRACK }), this.crackText);
      this.neckPips = r.necks.map(() => h('i'));
      this.neckRow.append(h('span.bh-ico', { html: NECK }), h('span.bh-label', {}, t('senda.isla.necks')), h('span.al-pips.vt-pips.ix-neckpips', {}, ...this.neckPips));
      this.rows.push(this.bandRow, this.keptRow, this.crackRow, this.neckRow, h('div.bh-row.vt-rule', {}, h('span.bh-ico', { html: CRACK }), h('span.vt-rule-t', {}, t('senda.isla.rule1'))));
      const c = this.make(0, CRACK, '', t('senda.isla.rule1'), 'crack');
      c.el.disabled = true;
      c.el.classList.add('ix-crackmark');
      this.marks.push(c);
      r.neckAt.forEach((p, k) => {
        const m = this.make(k, NECK, '', t('senda.isla.necks'), `neck-${k}`);
        m.el.disabled = true;
        m.el.classList.add('ix-neckmark');
        simToWorld(p.x, p.y, m.point, 0.4);
        this.marks.push(m);
      });
    } else if (ir?.lagoon) {
      const r = ir.lagoon;
      this.readRow.append(h('span.bh-ico', { html: CROSS }), h('span.bh-label', {}, t('senda.isla.crossings')), this.readText);
      this.wrongRow.append(h('span.bh-ico', { html: CRACK }), h('span.bh-label', {}, t('senda.isla.wrongs')), this.wrongText);
      this.wolfRow.append(h('span.bh-ico', { html: WOLF }), this.wolfText);
      this.rows.push(this.readRow, this.wrongRow, this.wolfRow, h('div.bh-row.vt-rule', {}, h('span.bh-ico', { html: BOOT }), h('span.vt-rule-t', {}, t('senda.isla.rule2'))));
      r.ends.forEach((p, l) => {
        const m = this.make(l, BOOT, letterName(l), t('senda.isla.endHint'), `end-${l}`);
        m.el.classList.add('ix-end');
        simToWorld(p.x, p.y, m.point, 0.3);
        this.marks.push(m);
      });
      r.crossings.forEach((c, i) => {
        const m = this.make(100 + i, CROSS, '', t('senda.isla.crossHint'), `cross-${i}`);
        m.el.disabled = true;
        m.el.classList.add('ix-cross');
        if (!lagoonHers(r, i)) m.el.classList.add('ix-beast');
        const chip = h('span.ix-over');
        m.el.append(chip);
        m.chip = chip;
        simToWorld(c.at.x, c.at.y, m.point, 0.25);
        this.marks.push(m);
      });
    } else if (ir?.seal) {
      const r = ir.seal;
      this.keptRow.append(h('span.bh-ico', { html: FIRE }), h('span.bh-label', {}, t('senda.isla.fire')), h('span.bar.vt-bar', {}, this.keptFill), this.keptText);
      this.sealRow.append(h('span.bh-ico', { html: SEAL }), h('span.bh-label', {}, t('senda.isla.seal')), h('span.bar.vt-bar.bt-elderbar', {}, this.sealFill), this.sealText);
      this.slideRow.append(h('span.bh-ico', { html: SEAL }), this.slideText);
      this.howlRow.append(h('span.bh-ico', { html: HOWL }), this.howlText);
      this.rows.push(this.sealRow, this.slideRow, this.keptRow, this.howlRow, h('div.bh-row.vt-rule', {}, h('span.bh-ico', { html: FIRE }), h('span.vt-rule-t', {}, t('senda.isla.rule3'))));
      r.holes.forEach((p, k) => {
        const m = this.make(k, HOLE, '', t('senda.isla.holeHint'), `hole-${k}`);
        m.el.disabled = true;
        m.el.classList.add('bt-mark', 'bt-tip', 'ix-hole');
        simToWorld(p.x, p.y, m.point, 0.3);
        this.marks.push(m);
      });
    }
    this.panel = h(`div.bh.cz-panel.vt-panel.bt-panel.ix-panel.ix-${this.kind}`, {}, ...this.rows);
  }

  private make(at: number, icon: string, name: string, hint: string, test: string): Mark {
    const state = h('small.al-state.cz-state');
    const el = h(
      `button.al-stone.cz-mark.vt-mark.ix-mark`,
      { type: 'button', 'data-test': `isla-${test}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      h('span.al-glyph', { html: icon }),
      name ? h('b.mn-name.cz-name', {}, name) : '',
      state,
    ) as HTMLButtonElement;
    this.board.append(el);
    return { el, state, point: new THREE.Vector3(), at, kind: test.split('-')[0] as string };
  }

  private setText(el: HTMLElement, s: string): void {
    if (el.textContent !== s) el.textContent = s;
  }

  private setState(m: Mark, s: string): void {
    if (m.state.textContent !== s) {
      m.state.textContent = s;
      remeasureMark(m.el);
    }
  }

  private kept(s: BattleState, id: string): void {
    const b = s.senda?.npcs.find((n) => n.id === id);
    if (!b) return;
    const k = Math.max(0, Math.min(1, b.hp / Math.max(1, b.maxHp)));
    setStyle(this.keptFill, 'width', `${(k * 100).toFixed(1)}%`);
    this.setText(this.keptText, t('senda.hud.hits', { n: b.hp }));
    this.keptRow.classList.toggle('warn', b.hp * 3 <= b.maxHp);
  }

  private updateFloes(s: BattleState): void {
    const r = s.rules?.senda?.isla?.floes;
    const w = s.senda?.isla?.floes;
    if (!r || !w) return;
    this.kept(s, 'camp');
    const down = w.done >= 0 ? r.bands.length : Math.max(0, w.band - 1);
    this.bandPips.forEach((p, i) => {
      p.classList.toggle('on', i < down);
      p.classList.toggle('now', i === w.band - 1 && w.done < 0);
    });
    const on = s.enemies.filter((e) => e.hp > 0).length;
    this.setText(
      this.bandText,
      w.next >= 0 ? t('senda.isla.bandIn', { s: Math.max(0, Math.ceil((w.next - s.waveT) / TICK_HZ)) }) : `${t('senda.isla.band', { n: Math.max(1, w.band), of: r.bands.length })} · ${t('senda.isla.onIce', { n: on })}`,
    );
    const c = w.crack;
    this.setText(this.crackText, c && !c.open ? t('senda.isla.crack', { s: Math.max(0, Math.ceil((c.opens - s.tick) / TICK_HZ)) }) : c ? t('senda.isla.crackOpen') : t('senda.isla.crackIn'));
    this.crackRow.classList.toggle('warn', !!c);
    w.necks.forEach((st, k) => {
      const p = this.neckPips[k];
      if (!p) return;
      p.classList.toggle('on', st === 0);
      p.classList.toggle('mid', st === 1);
      p.classList.toggle('bad', st === 2);
    });
    for (const m of this.marks) {
      if (m.kind === 'crack') {
        const gone = !c || s.result !== 'running';
        m.el.classList.toggle('gone', gone);
        m.el.classList.toggle('hot', !!c && !c.open);
        if (c) {
          simToWorld(c.at.x, c.at.y, m.point, 0.35);
          this.setState(m, c.open ? t('senda.isla.crackOpen') : t('senda.isla.crack', { s: Math.max(0, Math.ceil((c.opens - s.tick) / TICK_HZ)) }));
        }
        continue;
      }
      const st = neckStage(r, m.at, s.waveT);
      const gone = st === 0 || s.result !== 'running';
      m.el.classList.toggle('gone', gone);
      m.el.classList.toggle('hot', st === 1);
      m.el.classList.toggle('off', st === 2);
      if (!gone) this.setState(m, `${t(`senda.isla.neck.${st}`)} · ${t('senda.isla.neckIn', { s: Math.max(0, Math.ceil(neckLeft(r, m.at, s.waveT) / TICK_HZ)) })}`);
    }
  }

  private updateLagoon(s: BattleState): void {
    const r = s.rules?.senda?.isla?.lagoon;
    const w = s.senda?.isla?.lagoon;
    if (!r || !w) return;
    const hers = r.crossings.map((_, i) => i).filter((i) => lagoonHers(r, i));
    this.setText(this.readText, t('senda.isla.crossN', { n: hers.filter((i) => w.seen[i] === 1).length, of: hers.length }));
    this.setText(this.wrongText, w.wrongs === 0 ? t('senda.isla.wrong0') : t('senda.isla.wrongN', { n: w.wrongs }));
    this.wrongRow.classList.toggle('warn', w.wrongs > 0);
    this.setText(this.wolfText, t('senda.isla.wolvesIn', { s: Math.max(0, Math.ceil((w.next - s.waveT) / TICK_HZ)) }));
    const reading = w.reading >= 0 && w.seen[w.reading] === 0 ? w.reading : -1;
    const k = reading >= 0 ? Math.min(1, (s.tick - w.readAt) / Math.max(1, r.readT)) : 0;
    const key = `${w.seen.join('')}|${w.ends.join('')}|${w.goal}|${w.t0 >= 0}|${reading}|${w.done}|${s.result}`;
    for (const m of this.marks) if (m.kind === 'cross' && m.at - 100 === reading) m.el.style.setProperty('--k', k.toFixed(2));
    if (key === this.key) return;
    this.key = key;
    for (const m of this.marks) {
      if (m.kind === 'end') {
        const st = w.ends[m.at] ?? 0;
        const gone = st === 2 || w.done >= 0 || s.result !== 'running';
        m.el.classList.toggle('gone', gone);
        m.el.disabled = gone;
        m.el.classList.toggle('worn', st === 1);
        m.el.classList.toggle('sent', w.goal === m.at);
        this.setState(m, w.goal === m.at && w.t0 >= 0 ? t('senda.isla.breaking') : st === 1 ? t('senda.isla.cracked') : '');
        continue;
      }
      const i = m.at - 100;
      const c = r.crossings[i];
      const seen = w.seen[i] ?? 0;
      m.el.classList.toggle('gone', seen === 2 || !c || w.done >= 0 || s.result !== 'running');
      m.el.classList.toggle('read', seen === 1);
      m.el.classList.toggle('sent', i === reading);
      if (!c || !m.chip) continue;
      if (seen === 1) {
        const top = crossLetter(r.tracks, c.top);
        const low = crossLetter(r.tracks, c.top === c.a ? c.b : c.a);
        m.chip.replaceChildren(h('b.ix-top', {}, letterName(top)), h('span.ix-on', {}, '›'), h('s.ix-low', {}, letterName(low)));
        m.chip.title = t('senda.isla.over', { a: letterName(top), b: letterName(low) });
      } else m.chip.replaceChildren();
      this.setState(m, i === reading ? t('senda.isla.reading') : '');
    }
  }

  private updateSeal(s: BattleState): void {
    const r = s.rules?.senda?.isla?.seal;
    const w = s.senda?.isla?.seal;
    if (!r || !w) return;
    this.kept(s, 'fire');
    const e = s.enemies.find((x) => x.id === w.id);
    const k = e ? Math.max(0, e.hp) / Math.max(1, e.maxHp) : 0;
    setStyle(this.sealFill, 'width', `${(k * 100).toFixed(1)}%`);
    const st =
      w.phase === 1
        ? w.atFire
          ? t('senda.isla.sealAimFire')
          : t('senda.isla.sealAim')
        : w.phase === 2
          ? t('senda.isla.sealSlide')
          : w.phase === 3
            ? t('senda.isla.sealLook')
            : w.phase === 4
              ? t('senda.isla.sealGo')
              : w.phase === 5
                ? t('senda.isla.sealUnder', { s: Math.max(0, Math.ceil((w.until - s.tick) / TICK_HZ)) })
                : w.phase === 6
                  ? t('senda.isla.sealCut')
                  : t('senda.isla.sealFight');
    this.setText(this.sealText, w.phase === 0 || w.phase === 5 || w.phase === 6 ? st : '');
    this.sealRow.classList.toggle('warn', w.phase >= 3 && w.phase <= 5);
    this.sealRow.classList.toggle('go', w.phase === 6);
    // The slide's line: its warning, else its seconds.
    const aim = w.phase === 1 || w.phase === 2;
    const look = w.phase === 3 || w.phase === 4;
    this.setText(this.slideText, aim || look ? st : w.phase === 0 ? t('senda.isla.slideIn', { s: Math.max(0, Math.ceil((w.slideNext - s.waveT) / TICK_HZ)) }) : '');
    this.slideRow.classList.toggle('warn', aim || look);
    this.slideRow.classList.toggle('fire', w.phase === 1 && w.atFire === 1);
    const howl = w.howlers.some((x) => x.phase === 1 || x.phase === 2);
    const up = w.howlers.filter((x) => x.phase !== 3).length;
    this.setText(this.howlText, howl ? t('senda.isla.howl') : `${t('senda.isla.howlers')} · ${up}`);
    this.howlRow.classList.toggle('warn', howl);
    const key = `${w.phase}|${w.hole}|${s.result}`;
    if (key === this.key) return;
    this.key = key;
    for (const m of this.marks) {
      const target = look && w.hole === m.at;
      m.el.classList.toggle('gone', w.done >= 0 || s.result !== 'running');
      m.el.classList.toggle('target', target);
      this.setState(m, target ? t('senda.isla.sealLook') : '');
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'floes') this.updateFloes(s);
    else if (this.kind === 'lagoon') this.updateLagoon(s);
    else this.updateSeal(s);
  }

  /** Every frame: the marks over their places; the crack the Seal goes for held at the screen's edge. */
  place(_s: BattleState | undefined, camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      const target = m.el.classList.contains('target');
      if (this.v.z > 1 || (!target && (this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1))) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      const edge = target && (Math.abs(this.v.x) > 0.92 || Math.abs(this.v.y) > 0.92);
      m.el.classList.toggle('edge', edge);
      const x = (Math.max(-0.92, Math.min(0.92, this.v.x)) * 0.5 + 0.5) * w;
      const y = (-Math.max(-0.92, Math.min(0.92, this.v.y)) * 0.5 + 0.5) * ht;
      pinMark(m.el, Math.min(w - 40, Math.max(40, x)), y);
    }
  }
}
